/**
 * Screen lock, on-screen privacy and the activity log.
 *
 * The PIN is a screen lock, not encryption — it stops a passer-by using an
 * open laptop. Anyone with database access can still read the data, and the
 * copy below says so rather than implying more than it delivers.
 */
import { useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { weakHash } from '../../lib/format.js';
import { formatDate, pad2 } from '../../lib/dates.js';
import { Pill } from '../../components/Primitives.jsx';
import { Section, Grid, Row, NumberField, CheckGrid, Toggle } from './SettingsBits.jsx';

export default function SecurityPane() {
  const { S, patchSettings, updateSettings, lockNow, logAudit, toast } = useApp();

  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');

  const recent = (S.audit || []).slice().reverse().slice(0, 40);

  const savePin = () => {
    if (pin.length < 4 || pin.length > 8) { toast('Use 4 to 8 digits'); return; }
    if (pin !== confirm) { toast('The two PINs do not match'); return; }
    patchSettings({ pinHash: weakHash(pin) });
    logAudit('security', 'PIN set');
    setPin('');
    setConfirm('');
    toast('App lock is on');
  };

  const removePin = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove the app lock?')) return;
    patchSettings({ pinHash: '' });
    logAudit('security', 'PIN removed');
    toast('Lock removed');
  };

  const clearLog = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Clear the activity log?')) return;
    updateSettings(next => { next.audit = []; });
    toast('Log cleared');
  };

  return (
    <>
      <Section
        title="App lock"
        hint="Asks for a PIN before the workspace is shown. It keeps a passer-by out of an open laptop — it is not encryption, and it does not protect the data on the server."
      >
        <Grid>
          <div className="num">
            <label>{S.pinHash ? 'Change PIN' : 'Set a PIN'}</label>
            <input type="password" placeholder="4-8 digits" value={pin} onChange={e => setPin(e.target.value)} />
          </div>
          <div className="num">
            <label>Confirm</label>
            <input type="password" placeholder="repeat it" value={confirm} onChange={e => setConfirm(e.target.value)} />
          </div>
          <NumberField
            label="Auto-lock after"
            name="autoLock"
            unit="minutes idle · 0 is off"
            min={0}
            max={240}
          />
        </Grid>

        <Row>
          <button type="button" className="btn primary" onClick={savePin}>
            {S.pinHash ? 'Update PIN' : 'Turn on the lock'}
          </button>
          {S.pinHash ? (
            <>
              <button type="button" className="btn" onClick={lockNow}>Lock now</button>
              <button type="button" className="btn danger" onClick={removePin}>Remove the lock</button>
            </>
          ) : null}
        </Row>
        <p style={{ marginTop: 8 }}>Status: <b>{S.pinHash ? 'lock is on' : 'no lock set'}</b></p>
      </Section>

      <Section title="Privacy on screen">
        <CheckGrid>
          <Toggle label="Blur emails, phones and amounts until hovered" name="maskData" />
          <Toggle label="Keep an activity log" name="auditOn" />
        </CheckGrid>
        <p style={{ marginTop: 8 }}>Useful when you are sharing a screen or working in a café.</p>
      </Section>

      <Section title="Activity log" hint={`The last ${recent.length} actions taken in this workspace.`}>
        {recent.length ? (
          <div className="tscroll" style={{ maxHeight: 320 }}>
            <table>
              <thead>
                <tr><th>When</th><th>Who</th><th>Action</th><th>Record</th></tr>
              </thead>
              <tbody>
                {recent.map((x, i) => {
                  const at = new Date(x.t);
                  return (
                    <tr key={`${x.t}-${i}`}>
                      <td className="dim">
                        {formatDate(at, false, S.dateFmt)} {pad2(at.getHours())}:{pad2(at.getMinutes())}
                      </td>
                      <td>{x.who}</td>
                      <td><Pill tone="p-blue">{x.a}</Pill></td>
                      <td>{x.w}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="muted">Nothing logged yet.</p>}

        <Row>
          <button type="button" className="btn danger" onClick={clearLog}>Clear the log</button>
        </Row>
      </Section>
    </>
  );
}
